import { z } from "zod/v3";

import {
  AddressSchema,
  NullableNumberSchema,
  NullableStringSchema,
  OptionalAddressSchema,
  YesNoNullOptionsSchema
} from "@/features/facility/components/ContactDetails/forms/validation";
import { type CreateFacilityInput } from "../mutations";

const NullableNonNegativeNumberSchema = z.union([z.number().int().min(0), z.nan()]).nullable();

export const COURSE_TYPE_REQUIRED_FACILITY_TYPES = [
  "GOLF_COURSE_PRIVATE",
  "GOLF_COURSE_PUBLIC",
  "GOLF_RANGE",
  "INDOOR_FACILITY",
  "RESORT"
] as const;

export function isCourseTypeRequired(facilityType: string | null | undefined): boolean {
  return !!facilityType && (COURSE_TYPE_REQUIRED_FACILITY_TYPES as readonly string[]).includes(facilityType);
}

export const emptyCreateFacilityAddress = {
  address1: "",
  address2: "",
  city: "",
  countryCode: "",
  postalCode: "",
  state: "",
  addressType: ""
};

const defaultMainCreateFacilityAddress = {
  ...emptyCreateFacilityAddress,
  countryCode: "USA"
};

export const CreateFacilityBaseSchema = z.object({
  name: z.string().min(1, "Facility name is required"),
  sectionId: z.string().min(1, "Section is required"),
  chapterId: NullableStringSchema,
  facilityType: z.string().min(1, "Facility type is required"),
  operatingStatus: z.string().min(1, "Operating status is required"),
  website: z
    .string()
    .min(1, "Website is required")
    .url("Please enter a valid website URL starting with http:// or https://"),
  parentCompanyId: NullableStringSchema,
  mainAddress: AddressSchema,
  mailingAddress: OptionalAddressSchema,
  phone: z.string().min(1, "Business phone number is required"),
  fax: NullableStringSchema,
  golfShopPhone: NullableStringSchema,
  generalManagerName: NullableStringSchema,
  managementGroup: NullableStringSchema,
  courseType: NullableStringSchema,
  operationType: z.string().min(1, "Operation type is required"),
  lengthOfSeasonInMonths: NullableNumberSchema,
  holesInRegulation: NullableNonNegativeNumberSchema,
  practiceRangeAvailable: YesNoNullOptionsSchema,
  simulatorAvailable: YesNoNullOptionsSchema,
  golfCartBrand: NullableStringSchema,
  membershipTotal: NullableNonNegativeNumberSchema,
  membershipMen: NullableNonNegativeNumberSchema,
  membershipWomen: NullableNonNegativeNumberSchema,
  membershipJunior: NullableNonNegativeNumberSchema,
  membershipSenior: NullableNonNegativeNumberSchema
});

export const CreateFacilitySchema = CreateFacilityBaseSchema.superRefine((values, ctx) => {
  if (isCourseTypeRequired(values.facilityType) && !values.courseType) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Course type is required",
      path: ["courseType"]
    });
  }
});

export type CreateFacilityFormValues = z.input<typeof CreateFacilityBaseSchema>;
export type CreateFacilityFormSubmitValues = z.output<typeof CreateFacilitySchema>;
export type CreateFacilityFormInitialValues = Partial<CreateFacilityFormValues>;
export type CreateFacilityDuplicateCheckValues = Pick<
  CreateFacilityFormSubmitValues,
  "name" | "phone" | "sectionId" | "chapterId" | "mainAddress"
>;

function withMainAddressDefaults(
  mainAddress: Partial<CreateFacilityFormValues["mainAddress"]> = {}
): CreateFacilityFormValues["mainAddress"] {
  const merged = {
    ...defaultMainCreateFacilityAddress,
    ...mainAddress
  };

  if (!merged.countryCode && (merged.address1 || merged.city || merged.postalCode || merged.state)) {
    merged.countryCode = "USA";
  }

  return merged;
}

export const defaultCreateFacilityFormValues: CreateFacilityFormValues = {
  name: "",
  sectionId: "",
  chapterId: "",
  facilityType: "",
  operatingStatus: "",
  website: "",
  parentCompanyId: "",
  mainAddress: defaultMainCreateFacilityAddress,
  mailingAddress: emptyCreateFacilityAddress,
  phone: "",
  fax: "",
  golfShopPhone: "",
  generalManagerName: "",
  managementGroup: "",
  courseType: "",
  operationType: "",
  lengthOfSeasonInMonths: null,
  holesInRegulation: null,
  practiceRangeAvailable: null,
  simulatorAvailable: null,
  golfCartBrand: "",
  membershipTotal: null,
  membershipMen: null,
  membershipWomen: null,
  membershipJunior: null,
  membershipSenior: null
};

type MaybeValue = string | number | boolean | Record<string, unknown> | null | undefined;

export function getCreateFacilityFormValues(
  initialValues: CreateFacilityFormInitialValues = {}
): CreateFacilityFormValues {
  return {
    ...defaultCreateFacilityFormValues,
    ...initialValues,
    mainAddress: withMainAddressDefaults(initialValues.mainAddress),
    mailingAddress: {
      ...emptyCreateFacilityAddress,
      ...(initialValues.mailingAddress || {})
    }
  };
}

export function compactCreateFacilityInput(values: CreateFacilityFormSubmitValues): CreateFacilityInput {
  const input: Record<string, MaybeValue> = { name: values.name, sectionId: values.sectionId };

  (Object.keys(values) as (keyof typeof values)[]).forEach((key) => {
    if (key === "name" || key === "sectionId") return;
    const value = values[key];

    if (typeof value === "number" && Number.isNaN(value)) return;
    if (value == null || value === "") return;
    if (key === "mainAddress" && typeof value === "object") {
      input.mainAddress = { ...value, addressType: "main" };
      return;
    }
    if (key === "mailingAddress" && typeof value === "object") {
      input.mailingAddress = { ...value, addressType: "mailing" };
      return;
    }
    input[key] = value as MaybeValue;
  });

  return input as CreateFacilityInput;
}
